import * as React from 'react'
import { cn } from '../../lib/utils'

export interface OTPInputProps {
	length?: number
	value: string
	onChange: (value: string) => void
	autoFocus?: boolean
	disabled?: boolean
	error?: boolean
	className?: string
}

const OTPInput = React.forwardRef<HTMLDivElement, OTPInputProps>(
	({ length = 6, value, onChange, autoFocus = false, disabled = false, error = false, className }, ref) => {
		const inputRefs = React.useRef<(HTMLInputElement | null)[]>([])

		const digits = Array.from({ length }, (_, i) => value[i] ?? '')

		const focusAt = (index: number) => {
			inputRefs.current[index]?.focus()
		}

		const handleChange = (index: number, char: string) => {
			const digit = char.replace(/\D/g, '').slice(-1)
			const newDigits = [...digits]
			newDigits[index] = digit
			onChange(newDigits.join(''))
			if (digit && index < length - 1) {
				focusAt(index + 1)
			}
		}

		const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Backspace') {
				if (digits[index]) {
					const newDigits = [...digits]
					newDigits[index] = ''
					onChange(newDigits.join(''))
				} else if (index > 0) {
					focusAt(index - 1)
				}
			}
			if (e.key === 'ArrowLeft' && index > 0) focusAt(index - 1)
			if (e.key === 'ArrowRight' && index < length - 1) focusAt(index + 1)
		}

		const handlePaste = (e: React.ClipboardEvent) => {
			e.preventDefault()
			const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
			onChange(pasted.padEnd(length, '').slice(0, length))
			const nextEmpty = Math.min(pasted.length, length - 1)
			focusAt(nextEmpty)
		}

		return (
			<div
				ref={ref}
				className={cn(
					'inline-flex rounded-md overflow-hidden',
					error && 'shadow-[0_0_0_2px_var(--color-background),0_0_0_4px_var(--color-destructive-hover)]',
					className,
				)}
			>
				{digits.map((digit, index) => (
					<input
						key={index}
						ref={(el) => { inputRefs.current[index] = el }}
						type="text"
						inputMode="numeric"
						autoComplete={index === 0 ? 'one-time-code' : 'off'}
						maxLength={1}
						value={digit}
						autoFocus={autoFocus && index === 0}
						disabled={disabled}
						onChange={(e) => handleChange(index, e.target.value)}
						onKeyDown={(e) => handleKeyDown(index, e)}
						onPaste={handlePaste}
						onFocus={(e) => e.target.select()}
						className={cn(
							'size-10 border border-(--color-border) bg-(--color-background)',
							'text-center text-base font-semibold text-(--color-foreground)',
							'focus:outline-none focus:z-10 focus:border-(--color-focus)',
							'disabled:cursor-not-allowed disabled:opacity-60',
							'transition-colors relative',
							index === 0 && 'rounded-l-md',
							index === length - 1 && 'rounded-r-md',
							index > 0 && '-ml-px',
						)}
					/>
				))}
			</div>
		)
	}
)

OTPInput.displayName = 'OTPInput'

export { OTPInput }
